import React, { createContext, useContext, useEffect, useState } from "react";
import defaultTenant from "../config/defaultTenant";
import { applyThemeToCss, applyFavicon } from "../utils/applyTheme";

const TenantContext = createContext(defaultTenant);

function detectTenantSlug() {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  if (parts.length < 3) {
    return process.env.REACT_APP_DEFAULT_TENANT || "default";
  }
  return parts[0];
}

function TenantLoader() {
  return (
    <div
      style={{
        position: "fixed", inset: 0, display: "flex",
        alignItems: "center", justifyContent: "center",
        background: "#060B14", zIndex: 9999,
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            width: 48, height: 48,
            border: "3px solid rgba(99,102,241,0.3)",
            borderTopColor: "#6366f1", borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            margin: "0 auto 16px",
          }}
        />
        <p style={{ fontFamily: "monospace", fontSize: 12, color: "rgba(165,180,252,0.7)", letterSpacing: "0.1em" }}>
          INITIALIZING...
        </p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const slug = detectTenantSlug();

    const applyAndStore = (config) => {
      window.__tenantConfig = config;
      applyThemeToCss(config.theme);
      if (config.faviconUrl) applyFavicon(config.faviconUrl);
      if (config.brandName) document.title = config.brandName;
      setTenant(config);
    };

    if (slug === "default") {
      applyAndStore(defaultTenant);
      setLoading(false);
      return;
    }

    const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:3002/v1";
    fetch(`${baseUrl}/tenant/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error(`Tenant not found: ${slug}`);
        return r.json();
      })
      .then((data) => applyAndStore({ ...defaultTenant, ...data }))
      .catch((err) => {
        console.warn(`[TenantContext] Could not load tenant "${slug}":`, err.message, "— falling back to defaults.");
        applyAndStore(defaultTenant);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <TenantLoader />;

  return (
    <TenantContext.Provider value={tenant}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const ctx = useContext(TenantContext);
  if (!ctx) return defaultTenant;
  return ctx;
};

export default TenantContext;
